import {
  ComposedChart,
  Area,
  Bar,
  Cell,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { fmtM } from "@shared-core/domain/format-money";
import { quoteTemperature } from "@shared-core/domain/quote-funnel";
import type {
  QuoteFunnelBucket,
  WinCurvePoint,
} from "@shared-core/data/api-types";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../../../primitives/chart";

// A bucket's win-rate curve (the "weight") with its open quotes overlaid as
// volume bars (the "£"), one bar per quote at its age. Bars fade as the quote
// cools — opacity encodes how much win rate is left — so tall-but-faint bars
// read as cold volume and tall-solid bars as live pipeline. Forecast-domain
// pink; house ChartContainer for the styled tooltip.
const X_MAX = 60;
const PINK = "#f472b6";
const AXIS = "#737373";
const NEUTRAL = "#a3a3a3";

function interp(curve: WinCurvePoint[], age: number): number {
  if (age <= curve[0].age_wd) return curve[0].cumulative;
  for (let i = 1; i < curve.length; i++) {
    if (age <= curve[i].age_wd) {
      const a = curve[i - 1];
      const b = curve[i];
      const t = (age - a.age_wd) / (b.age_wd - a.age_wd || 1);
      return a.cumulative + t * (b.cumulative - a.cumulative);
    }
  }
  return curve[curve.length - 1].cumulative;
}

const TEMP_LABEL = { hot: "hot", warm: "lukewarm", cold: "cold" } as const;

const chartConfig = {
  cumPct: { label: "Win rate", color: PINK },
  amount: { label: "Quoted", color: PINK },
} satisfies ChartConfig;

export function WinRateCurveChart({ bucket }: { bucket: QuoteFunnelBucket }) {
  const plateauPct = Math.round(bucket.plateau_pct);
  const winCurve = bucket.win_curve ?? [];

  // Sum quote £ per age, keeping the coolest temperature seen at that age.
  const byAge = new Map<number, number>();
  for (const q of bucket.quotes ?? []) {
    if (q.age_wd <= X_MAX)
      byAge.set(q.age_wd, (byAge.get(q.age_wd) ?? 0) + q.amount);
  }
  const maxAmount = Math.max(...byAge.values(), 1);

  // One row per age (curve anchors ∪ quote ages): cumPct drives the curve,
  // amount drives the bar. Single dataset so both series render reliably.
  const ages = Array.from(
    new Set<number>([
      ...winCurve.filter((p) => p.age_wd <= X_MAX).map((p) => p.age_wd),
      ...byAge.keys(),
    ]),
  ).sort((a, b) => a - b);
  const data = ages.map((ageWd) => ({
    ageWd,
    cumPct: interp(winCurve, ageWd) * 100,
    amount: byAge.get(ageWd) ?? null,
    temp: TEMP_LABEL[quoteTemperature(ageWd)],
    opacity: Math.max(0.15, 1 - ageWd / 45), // fade with age — hot solid, cold faint
  }));

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Plateau{" "}
          <span className="font-medium text-foreground">{plateauPct}%</span> ·
          reached by ~{bucket.window_wd} working days
        </span>
        <span>
          median age{" "}
          <span className="font-medium text-foreground">
            {bucket.median_age_wd}wd
          </span>
        </span>
      </div>

      <ChartContainer config={chartConfig} className="aspect-auto h-40 w-full">
        <ComposedChart
          data={data}
          margin={{ top: 12, right: 8, bottom: 0, left: 0 }}
        >
          <defs>
            <linearGradient id="winCurveFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={PINK} stopOpacity={0.16} />
              <stop offset="100%" stopColor={PINK} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            type="number"
            dataKey="ageWd"
            domain={[0, X_MAX]}
            ticks={[0, 7, 14, 21, 30, 45, 60]}
            tick={{ fontSize: 10, fill: AXIS }}
            tickFormatter={(v: number) => `${v}wd`}
            axisLine={{ stroke: "#e5e5e5" }}
            tickLine={false}
          />
          <YAxis
            yAxisId="pct"
            hide
            domain={[0, Math.max(60, plateauPct + 10)]}
          />
          <YAxis yAxisId="vol" hide domain={[0, maxAmount * 1.05]} />
          <ChartTooltip
            cursor={{ stroke: "#e5e5e5" }}
            content={
              <ChartTooltipContent
                labelFormatter={(_, payload) => {
                  const p = payload?.[0]?.payload;
                  return p ? `${p.ageWd}wd old · ${p.temp}` : "";
                }}
                formatter={(value, _name, item) => {
                  if (value == null) return null;
                  const isAmount = item?.dataKey === "amount";
                  return (
                    <div className="flex w-full items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                        style={{
                          background: PINK,
                          opacity: isAmount ? 1 : 0.35,
                        }}
                      />
                      <span className="text-muted-foreground">
                        {isAmount ? "Quoted" : "Cumulative win-rate"}
                      </span>
                      <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
                        {isAmount
                          ? fmtM(Number(value))
                          : `${Number(value).toFixed(0)}%`}
                      </span>
                    </div>
                  );
                }}
              />
            }
          />
          <ReferenceLine
            yAxisId="pct"
            x={bucket.window_wd}
            stroke="#d4d4d4"
            strokeDasharray="3 3"
            label={{
              value: "flattens",
              position: "insideTopRight",
              fontSize: 9,
              fill: AXIS,
            }}
          />
          <ReferenceLine
            yAxisId="pct"
            y={plateauPct}
            stroke={NEUTRAL}
            strokeDasharray="4 3"
            label={{
              value: "plateau",
              position: "insideBottomRight",
              fontSize: 9,
              fill: AXIS,
            }}
          />
          {/* Win-rate curve first (background) */}
          <Area
            yAxisId="pct"
            type="monotone"
            dataKey="cumPct"
            stroke={PINK}
            strokeWidth={2}
            fill="url(#winCurveFill)"
            dot={false}
            isAnimationActive={false}
          />
          {/* Volume bars on top — one per quote age, fading as they cool */}
          <Bar
            yAxisId="vol"
            dataKey="amount"
            barSize={9}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          >
            {data.map((d, i) => (
              <Cell key={i} fill={PINK} fillOpacity={d.opacity} />
            ))}
          </Bar>
        </ComposedChart>
      </ChartContainer>

      <div className="text-xs text-muted-foreground space-y-0.5">
        <div>Bars = £ quoted, fading with age</div>
        <div>Line = cumulative win-rate</div>
      </div>
    </div>
  );
}
